import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Select,
  Spin,
  Table,
  Typography,
} from 'antd';
import React, { useEffect, useState } from 'react';
import { Agent, AgentFeature, ClientFeatures, FeatureValue } from '../../types';

interface AgentOverridesProps {
  agent: Agent;
  apiUrl: string;
  accessToken: string;
  clientId: string;
  onAgentUpdated: () => Promise<void>;
  onClose: () => void;
  visible: boolean;
  clientFeatures?: ClientFeatures;
}

interface AgentFeatureForm {
  features: {
    id?: string;
    feature_enabled: 'enabled' | 'disabled' | 'none';
    feature_name: string;
  }[];
}

const ESCALATIONS_ONLY_FEATURES = [
  'END_OF_DAY',
  'PROCESS_RECORDINGS',
  'AGENT_PLAYBOOK',
  'LEAD_STAGE_CHANGE',
  'CREATE_APPOINTMENTS',
] as const;

const formatFeatureValue = (value: FeatureValue | string): string => {
  switch (value) {
    case FeatureValue.ALL:
    case FeatureValue.LIVE:
    case FeatureValue.ENABLED:
      return 'On';
    case FeatureValue.DISABLED:
      return 'Off';
    case FeatureValue.AGENTS:
      return 'Specific Agents';
    case FeatureValue.SHADOW:
      return 'Shadow';
    default:
      return value as string;
  }
};

export const AgentOverrides: React.FC<AgentOverridesProps> = ({
  agent,
  apiUrl,
  accessToken,
  clientId: clientConfigId,
  onAgentUpdated,
  onClose,
  visible,
  clientFeatures,
}) => {
  const [loading, setLoading] = useState(false);
  const [configuringEscalations, setConfiguringEscalations] = useState(false);
  const [error, setError] = useState<string>();
  const [success, setSuccess] = useState(false);
  const [features, setFeatures] = useState<AgentFeature[]>([]);
  const [search, setSearch] = useState('');
  const query = search.trim().toLowerCase();

  const fetchData = async (): Promise<boolean> => {
    setLoading(true);
    setError(undefined);
    try {
      const response = await fetch(
        `${apiUrl}/api/agents/${agent.id}/features`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(`Failed to fetch features: ${data.message}`);
      }

      setFeatures(
        (data as AgentFeature[]).filter(feature => !feature.deprecated),
      );
      return true;
    } catch (err) {
      setError(`${err}`);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = async (values: AgentFeatureForm) => {
    setLoading(true);
    setError(undefined);
    setSuccess(false);
    try {
      const body = values.features
        .filter(v => v.id || v.feature_enabled !== 'none')
        .map(v => ({
          id: v.id,
          feature_name: v.feature_name,
          feature_enabled:
            v.feature_enabled === 'enabled'
              ? true
              : v.feature_enabled === 'disabled'
                ? false
                : null,
        }));

      const response = await fetch(
        `${apiUrl}/api/agents/${agent.id}/features`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(body),
        },
      );

      if (!response.ok) {
        throw new Error('Failed to update feature overrides');
      }

      await fetchData();
    } catch (err) {
      setError(`${err}`);
    } finally {
      setLoading(false);
    }
  };

  const configureForEscalationsOnly = async () => {
    setConfiguringEscalations(true);
    setError(undefined);
    setSuccess(false);
    try {
      const featuresResponse = await fetch(
        `${apiUrl}/api/agents/${agent.id}/features`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(
            ESCALATIONS_ONLY_FEATURES.map(featureName => ({
              feature_name: featureName,
              feature_enabled: false,
            })),
          ),
        },
      );

      if (!featuresResponse.ok) {
        throw new Error('Failed to configure feature overrides');
      }

      const agentResponse = await fetch(
        `${apiUrl}/api/agents/${clientConfigId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify([{ id: agent.id, is_isa: true }]),
        },
      );

      if (!agentResponse.ok) {
        await fetchData();
        throw new Error(
          'Feature overrides updated, but ISA could not be enabled',
        );
      }

      const featuresRefreshed = await fetchData();
      await onAgentUpdated();
      if (featuresRefreshed) {
        setSuccess(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setConfiguringEscalations(false);
    }
  };

  useEffect(() => {
    if (visible) {
      setSuccess(false);
      void fetchData();
    }
  }, [visible]);

  return (
    <Modal
      footer={null}
      onCancel={onClose}
      open={visible}
      title={`Feature Overrides - ${agent.first_name} ${agent.last_name}`}
      width={600}
    >
      {error && (
        <Alert
          message={error}
          showIcon
          style={{ marginBottom: 16 }}
          type="error"
        />
      )}
      {success && (
        <Alert
          description={
            <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
              <li>ISA enabled</li>
              <li>END_OF_DAY disabled</li>
              <li>PROCESS_RECORDINGS disabled</li>
              <li>AGENT_PLAYBOOK disabled</li>
              <li>LEAD_STAGE_CHANGE disabled</li>
              <li>CREATE_APPOINTMENTS disabled</li>
            </ul>
          }
          message="Update successful"
          showIcon
          style={{ marginBottom: 16 }}
          type="success"
        />
      )}
      {loading ? (
        <Spin />
      ) : (
        <Form
          disabled={loading || configuringEscalations}
          initialValues={{
            features: features.map(feature => ({
              id: feature.id,
              feature_name: feature.feature_name,
              feature_enabled:
                feature.feature_enabled === null
                  ? 'none'
                  : feature.feature_enabled
                    ? 'enabled'
                    : 'disabled',
            })),
          }}
          onFinish={(values: AgentFeatureForm) => {
            void handleFinish(values);
          }}
        >
          <div style={{ marginBottom: 16 }}>
            <Button
              disabled={loading || configuringEscalations}
              loading={configuringEscalations}
              onClick={() => {
                void configureForEscalationsOnly();
              }}
            >
              Configure for Lead Engagement Escalations Only (auto-saves)
            </Button>
          </div>
          <Input
            allowClear
            onChange={e => setSearch(e.target.value)}
            onPressEnter={e => e.preventDefault()}
            placeholder="Search features"
            style={{ marginBottom: 16 }}
            value={search}
          />
          <div
            style={{ maxHeight: '400px', overflowY: 'auto', marginBottom: 16 }}
          >
            <Table
              dataSource={features}
              // Hide rather than filter: the form submits only mounted rows, keyed by index.
              onRow={feature => ({
                style: feature.feature_name.toLowerCase().includes(query)
                  ? undefined
                  : { display: 'none' },
              })}
              pagination={false}
              rowKey="feature_name"
              size="small"
            >
              <Table.Column
                dataIndex="feature_name"
                key="feature_name"
                render={(text, feature: AgentFeature, index) => (
                  <>
                    <Form.Item
                      hidden
                      initialValue={feature.id}
                      name={['features', index, 'id']}
                    >
                      <input type="hidden" />
                    </Form.Item>
                    <Form.Item
                      hidden
                      initialValue={feature.feature_name}
                      name={['features', index, 'feature_name']}
                    >
                      <input type="hidden" />
                    </Form.Item>
                    <Typography.Text>{text}</Typography.Text>
                  </>
                )}
                title="Feature"
              />
              <Table.Column
                key="override"
                render={(_, feature: AgentFeature, index) => (
                  <Form.Item
                    initialValue={
                      feature.feature_enabled === null
                        ? 'none'
                        : feature.feature_enabled
                          ? 'enabled'
                          : 'disabled'
                    }
                    name={['features', index, 'feature_enabled']}
                    style={{ marginBottom: 0 }}
                  >
                    <Select
                      options={[
                        {
                          value: 'none',
                          label: (() => {
                            const defaultValue =
                              clientFeatures?.[
                                feature.feature_name as keyof ClientFeatures
                              ];
                            if (defaultValue === undefined) {
                              return 'None';
                            }
                            return `None (Default: ${formatFeatureValue(defaultValue)})`;
                          })(),
                        },
                        { value: 'enabled', label: 'On' },
                        { value: 'disabled', label: 'Off' },
                      ]}
                    />
                  </Form.Item>
                )}
                title="Override"
              />
              {clientFeatures && (
                <Table.Column
                  key="client_value"
                  render={(_, feature: AgentFeature) => {
                    const defaultValue =
                      clientFeatures[
                        feature.feature_name as keyof ClientFeatures
                      ];
                    if (defaultValue === undefined) {
                      return null;
                    }

                    return (
                      <Typography.Text type="secondary">
                        {formatFeatureValue(defaultValue)}
                      </Typography.Text>
                    );
                  }}
                  title="Client Value"
                />
              )}
            </Table>
          </div>
          <Button disabled={loading} htmlType="submit" type="primary">
            Save
          </Button>
        </Form>
      )}
    </Modal>
  );
};
